import { useEffect, useState, useRef } from "react";
import { Plus, Edit, Trash2, X, Save, Upload, Star, Flame, ImagePlus, Search } from "lucide-react";
import {
  adminListMenu,
  adminCreateMenuItem,
  adminUpdateMenuItem,
  adminDeleteMenuItem,
  adminUploadImage,
  resolveImageUrl,
} from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CATEGORIES = [
  { value: "wings", label: "Wings" },
  { value: "burgers", label: "Burgers" },
  { value: "philly", label: "Philly" },
  { value: "seafood", label: "Seafood" },
  { value: "nachos", label: "Nachos" },
  { value: "appetizers", label: "Finger Foods" },
  { value: "pasta", label: "Pasta" },
  { value: "quesadillas", label: "Quesadillas" },
  { value: "salads", label: "Salads & Wraps" },
  { value: "sides", label: "Fries & Sides" },
  { value: "desserts", label: "Desserts" },
  { value: "drinks", label: "Drinks" },
];

const EMPTY = {
  name: "",
  description: "",
  price: 0,
  category: "wings",
  image_url: "",
  popular: false,
  spicy: false,
};

export const AdminMenuManager = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [editing, setEditing] = useState(null); // {id?, ...form}
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListMenu(token);
      setItems(data.items || []);
    } catch {
      toast.error("Couldn't load menu items");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const filtered = items.filter((i) => {
    if (catFilter !== "all" && i.category !== catFilter) return false;
    if (query) {
      const q = query.toLowerCase();
      if (!i.name.toLowerCase().includes(q) && !(i.description || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const grouped = CATEGORIES.reduce((acc, c) => {
    acc[c.value] = filtered.filter((i) => i.category === c.value);
    return acc;
  }, {});

  const openNew = () => setEditing({ ...EMPTY });
  const openEdit = (item) => setEditing({ ...item });
  const closeEditor = () => setEditing(null);

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.name.trim()) return toast.error("Name is required");
    if (!editing.description.trim()) return toast.error("Description is required");
    if (Number(editing.price) < 0 || Number.isNaN(Number(editing.price))) return toast.error("Price must be a number");

    setSaving(true);
    const payload = {
      name: editing.name.trim(),
      description: editing.description.trim(),
      price: Number(editing.price),
      category: editing.category,
      image_url: editing.image_url || null,
      popular: !!editing.popular,
      spicy: !!editing.spicy,
    };
    try {
      if (editing.id) {
        await adminUpdateMenuItem(editing.id, payload, token);
        toast.success("Item updated");
      } else {
        await adminCreateMenuItem(payload, token);
        toast.success("Item created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    try {
      await adminDeleteMenuItem(item.id, token);
      toast.success("Item deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = await adminUploadImage(file, token);
      setEditing((cur) => ({ ...cur, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid="admin-menu-manager">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              data-testid="menu-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search items…"
              className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-10 pl-9 text-white"
            />
          </div>
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger
              data-testid="menu-cat-filter"
              className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-10 text-white uppercase tracking-wider text-xs w-44"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-2 border-[#333] text-white rounded-none">
              <SelectItem value="all" className="uppercase text-xs tracking-wider">All Categories</SelectItem>
              {CATEGORIES.map((c) => (
                <SelectItem key={c.value} value={c.value} className="uppercase text-xs tracking-wider">
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <button
          data-testid="add-item-btn"
          onClick={openNew}
          className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF3333] text-white font-bold uppercase tracking-widest text-xs px-4 h-10 border-2 border-transparent hover:border-white"
        >
          <Plus className="w-4 h-4" strokeWidth={3} /> Add New Item
        </button>
      </div>

      {loading ? (
        <div className="text-center py-20 text-white/50 uppercase tracking-widest">Loading menu…</div>
      ) : (
        <div className="space-y-8">
          {CATEGORIES.map((c) => {
            const list = grouped[c.value];
            if (!list || list.length === 0) return null;
            return (
              <section key={c.value} data-testid={`menu-section-${c.value}`}>
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="font-heading text-2xl text-white">{c.label}</h3>
                  <span className="text-[10px] uppercase tracking-widest text-white/40 font-bold bg-black px-2 py-0.5 border border-[#333]">
                    {list.length}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {list.map((item) => (
                    <article
                      key={item.id}
                      data-testid={`menu-item-card-${item.id}`}
                      className="border-2 border-[#333] bg-[#1A1A1A] hover:border-[#E60000] transition-colors p-3 flex gap-3"
                    >
                      <div className="w-20 h-20 flex-shrink-0 bg-black border border-[#333] overflow-hidden">
                        {item.image_url ? (
                          <img src={resolveImageUrl(item.image_url)} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white/30">
                            <ImagePlus className="w-6 h-6" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 flex-wrap mb-1">
                          <h4 className="font-heading text-base text-white leading-tight truncate">{item.name}</h4>
                          {item.popular && <Star className="w-3 h-3 fill-[#E60000] text-[#E60000]" strokeWidth={0} />}
                          {item.spicy && <Flame className="w-3 h-3 text-[#E60000]" strokeWidth={2.5} />}
                        </div>
                        <div className="text-[#E60000] font-bold text-sm mb-1">${Number(item.price).toFixed(2)}</div>
                        <p className="text-white/60 text-xs line-clamp-2">{item.description}</p>
                      </div>
                      <div className="flex flex-col gap-1">
                        <button
                          data-testid={`edit-item-${item.id}`}
                          onClick={() => openEdit(item)}
                          className="w-8 h-8 flex items-center justify-center bg-[#0A0A0A] border border-[#333] hover:border-[#E60000] hover:text-[#E60000] text-white/70"
                          aria-label="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`delete-item-${item.id}`}
                          onClick={() => handleDelete(item)}
                          className="w-8 h-8 flex items-center justify-center bg-[#0A0A0A] border border-[#333] hover:bg-[#E60000] hover:border-[#E60000] text-white/70 hover:text-white"
                          aria-label="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-center py-20 text-white/50 uppercase tracking-widest border border-[#333] bg-[#1A1A1A]">
              No items match your filter
            </div>
          )}
        </div>
      )}

      {/* Editor Modal */}
      {editing && (
        <div
          data-testid="menu-editor-modal"
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeEditor}
        >
          <div
            className="bg-[#141414] border-2 border-[#E60000] max-w-2xl w-full max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b-2 border-[#333] bg-black sticky top-0 z-10">
              <h3 className="font-heading text-2xl text-white">
                {editing.id ? "Edit Item" : "New Item"}
              </h3>
              <button
                data-testid="menu-editor-close"
                onClick={closeEditor}
                className="w-9 h-9 flex items-center justify-center text-white/70 hover:text-white hover:bg-[#E60000]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              {/* Image */}
              <div>
                <label className="text-xs uppercase tracking-widest text-white/70 font-bold mb-2 block">Photo</label>
                <div className="flex items-center gap-4">
                  <div className="w-28 h-28 bg-black border-2 border-[#333] overflow-hidden flex-shrink-0">
                    {editing.image_url ? (
                      <img src={resolveImageUrl(editing.image_url)} alt="preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/30">
                        <ImagePlus className="w-8 h-8" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      onChange={handleUpload}
                      className="hidden"
                      data-testid="menu-image-file"
                    />
                    <button
                      type="button"
                      data-testid="menu-image-upload-btn"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="inline-flex items-center gap-2 bg-[#0A0A0A] border-2 border-[#333] hover:border-[#E60000] text-white px-3 h-10 uppercase tracking-widest text-[10px] font-bold disabled:opacity-50"
                    >
                      <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload Photo"}
                    </button>
                    <Input
                      data-testid="menu-image-url"
                      value={editing.image_url || ""}
                      onChange={(e) => setEditing((c) => ({ ...c, image_url: e.target.value }))}
                      placeholder="Or paste an image URL"
                      className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-10 text-white text-sm"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-white/70 font-bold mb-2 block">Name</label>
                <Input
                  data-testid="menu-name"
                  value={editing.name}
                  onChange={(e) => setEditing((c) => ({ ...c, name: e.target.value }))}
                  className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-11 text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/70 font-bold mb-2 block">Price ($)</label>
                  <Input
                    data-testid="menu-price"
                    type="number"
                    step="0.25"
                    min="0"
                    value={editing.price}
                    onChange={(e) => setEditing((c) => ({ ...c, price: e.target.value }))}
                    className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-11 text-white"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/70 font-bold mb-2 block">Category</label>
                  <Select value={editing.category} onValueChange={(v) => setEditing((c) => ({ ...c, category: v }))}>
                    <SelectTrigger
                      data-testid="menu-category"
                      className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-11 text-white uppercase tracking-wider text-xs"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-2 border-[#333] text-white rounded-none">
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value} className="uppercase text-xs tracking-wider">
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-white/70 font-bold mb-2 block">Description / Ingredients</label>
                <Textarea
                  data-testid="menu-description"
                  rows={5}
                  value={editing.description}
                  onChange={(e) => setEditing((c) => ({ ...c, description: e.target.value }))}
                  className="bg-[#0A0A0A] border-2 border-[#333] rounded-none text-white"
                />
              </div>

              <div className="flex items-center gap-6">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    data-testid="menu-popular"
                    type="checkbox"
                    checked={!!editing.popular}
                    onChange={(e) => setEditing((c) => ({ ...c, popular: e.target.checked }))}
                    className="w-4 h-4 accent-[#E60000]"
                  />
                  <span className="text-xs uppercase tracking-widest text-white/70 font-bold inline-flex items-center gap-1">
                    <Star className="w-3 h-3" /> Popular
                  </span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    data-testid="menu-spicy"
                    type="checkbox"
                    checked={!!editing.spicy}
                    onChange={(e) => setEditing((c) => ({ ...c, spicy: e.target.checked }))}
                    className="w-4 h-4 accent-[#E60000]"
                  />
                  <span className="text-xs uppercase tracking-widest text-white/70 font-bold inline-flex items-center gap-1">
                    <Flame className="w-3 h-3" /> Spicy
                  </span>
                </label>
              </div>
            </div>

            <div className="px-6 py-4 border-t-2 border-[#333] bg-black sticky bottom-0 flex justify-end gap-2">
              <button
                data-testid="menu-editor-cancel"
                onClick={closeEditor}
                className="bg-transparent border-2 border-[#333] hover:border-[#E60000] text-white/70 hover:text-white px-4 h-10 uppercase tracking-widest text-xs font-bold"
              >
                Cancel
              </button>
              <button
                data-testid="menu-editor-save"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF3333] text-white px-5 h-10 uppercase tracking-widest text-xs font-bold border-2 border-transparent hover:border-white disabled:opacity-50"
              >
                <Save className="w-4 h-4" /> {saving ? "Saving…" : editing.id ? "Save Changes" : "Create Item"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
